// Aba "Vínculos NF-e" dos cadastros: o de-para aprendido na importação do XML (fornecedor +
// código do produto no fornecedor → produto do estoque × fator, ou "não é estoque"). Corrigir
// aqui muda só a sugestão das próximas notas; as NFs já lançadas não mudam.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Pencil, Search, Trash2 } from "lucide-react";
import { deleteEstoqueVinculoNF, getEstoqueVinculosNF, putEstoqueVinculoNF } from "../../api";
import { ProdutoSelect, mensagemErro } from "./comum";
import { AvisoErro, Campo, Modal, NumeroInput, btnStyle, formatQtd, inputStyle, numeroParaInput, parseNumero } from "./ui";

function ModalVinculo({ C, vinculo, produtos, unidades, onClose, onSalvo }) {
  const [form, setForm] = useState({
    ignorar: vinculo.ignorar,
    produto_id: vinculo.produto_id,
    unidade_nf_id: vinculo.unidade_nf_id ? String(vinculo.unidade_nf_id) : "",
    fator: numeroParaInput(vinculo.fator_conversao),
  });
  const [erro, setErro] = useState("");
  const [salvando, setSalvando] = useState(false);
  const produto = produtos.find((p) => p.id === form.produto_id);
  const unidadeNF = unidades.find((u) => u.id === Number(form.unidade_nf_id));

  async function salvar() {
    if (!form.ignorar && (!form.produto_id || !form.unidade_nf_id || !(Number(parseNumero(form.fator)) > 0))) {
      setErro("Informe produto, unidade da NF e fator (> 0).");
      return;
    }
    setSalvando(true);
    try {
      onSalvo(await putEstoqueVinculoNF(vinculo.id, form.ignorar ? { ignorar: true } : {
        ignorar: false, produto_id: form.produto_id, unidade_nf_id: Number(form.unidade_nf_id), fator_conversao: parseNumero(form.fator),
      }));
    } catch (e) {
      setErro(mensagemErro(e));
      setSalvando(false);
    }
  }

  return (
    <Modal C={C} titulo="Editar vínculo" onClose={onClose}
      rodape={<>
        <button onClick={onClose} style={btnStyle(C.muted, true)}>Cancelar</button>
        <button onClick={salvar} disabled={salvando} style={{ ...btnStyle(C.brand), opacity: salvando ? 0.6 : 1 }}>Salvar</button>
      </>}>
      <AvisoErro C={C} onClose={() => setErro("")}>{erro}</AvisoErro>
      <div style={{ fontSize: 13, marginBottom: 12 }}>
        <strong>{vinculo.descricao_nf}</strong>
        <div style={{ color: C.muted, fontSize: 12 }}>{vinculo.fornecedor_nome} · cód. {vinculo.codigo_fornecedor}{vinculo.ean ? ` · EAN ${vinculo.ean}` : ""} · vem como {vinculo.unidade_xml}</div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
          <input type="checkbox" checked={form.ignorar} onChange={(e) => setForm({ ...form, ignorar: e.target.checked })} style={{ width: 18, height: 18 }} />
          Não é estoque (ignorar nas próximas notas)
        </label>
        {!form.ignorar && (
          <>
            <Campo label="Produto do estoque" C={C} obrigatorio>
              <ProdutoSelect C={C} produtos={produtos} value={form.produto_id} onChange={(id) => setForm({ ...form, produto_id: id })} />
            </Campo>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12 }}>
              <Campo label="Unidade da NF" C={C} obrigatorio>
                <select value={form.unidade_nf_id} onChange={(e) => setForm({ ...form, unidade_nf_id: e.target.value })} style={inputStyle(C)}>
                  <option value="">—</option>
                  {unidades.map((u) => <option key={u.id} value={u.id}>{u.sigla}</option>)}
                </select>
              </Campo>
              <Campo label={produto ? `Fator (${produto.unidade_padrao_sigla} por ${unidadeNF?.sigla || "emb."})` : "Fator"} C={C} obrigatorio>
                <NumeroInput C={C} casas={6} value={form.fator} onChange={(v) => setForm({ ...form, fator: v })} />
              </Campo>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

export default function AbaVinculosNF({ C, perm, produtos, unidades }) {
  const [vinculos, setVinculos] = useState([]);
  const [busca, setBusca] = useState("");
  const [erro, setErro] = useState("");
  const [editando, setEditando] = useState(null);

  const carregar = useCallback(() => getEstoqueVinculosNF().then(setVinculos).catch((e) => setErro(e.message)), []);
  useEffect(() => { carregar(); }, [carregar]);

  const filtrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return vinculos;
    return vinculos.filter((v) => [v.descricao_nf, v.fornecedor_nome, v.produto_nome, v.codigo_fornecedor, v.ean]
      .some((t) => (t || "").toLowerCase().includes(termo)));
  }, [vinculos, busca]);

  async function excluir(v) {
    if (!window.confirm(`Apagar o vínculo de "${v.descricao_nf}"? A próxima nota vai pedir a escolha de novo.`)) return;
    try {
      await deleteEstoqueVinculoNF(v.id);
      setVinculos((l) => l.filter((x) => x.id !== v.id));
    } catch (e) {
      setErro(mensagemErro(e));
    }
  }

  return (
    <>
      <div style={{ fontSize: 13, color: C.muted, marginBottom: 12, maxWidth: 760 }}>
        Como cada produto de cada fornecedor entra no estoque. É preenchido sozinho ao lançar um XML de NF-e; aqui você corrige ou apaga. Um mesmo produto do estoque pode ter vários vínculos (marcas, fornecedores e EANs diferentes).
      </div>
      <AvisoErro C={C} onClose={() => setErro("")}>{erro}</AvisoErro>
      <div style={{ position: "relative", maxWidth: 360, marginBottom: 12 }}>
        <Search size={15} color={C.muted} style={{ position: "absolute", left: 12, top: 13 }} />
        <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar por descrição, fornecedor, produto, EAN" style={{ ...inputStyle(C), paddingLeft: 34 }} />
      </div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>{filtrados.length} vínculo(s)</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {filtrados.length === 0 && (
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24, textAlign: "center", color: C.muted, fontSize: 13 }}>
            {vinculos.length ? "Nenhum vínculo encontrado." : "Nenhum vínculo ainda — eles aparecem depois do primeiro XML lançado."}
          </div>
        )}
        {filtrados.map((v) => (
          <div key={v.id} style={{ display: "flex", alignItems: "center", gap: 12, background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, wordBreak: "break-word" }}>{v.descricao_nf}</div>
              <div style={{ fontSize: 11, color: C.muted }}>{v.fornecedor_nome} · cód. {v.codigo_fornecedor}{v.ean ? ` · EAN ${v.ean}` : ""}</div>
              <div style={{ fontSize: 13, marginTop: 4, color: v.ignorar ? C.muted : C.text }}>
                {v.ignorar ? "→ não é estoque" : <>→ <strong>{v.produto_nome}</strong> · 1 {v.unidade_nf_sigla} = {formatQtd(v.fator_conversao)} {v.unidade_padrao_sigla}</>}
              </div>
            </div>
            {perm.financeiro && (
              <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                <button onClick={() => setEditando(v)} aria-label="Editar vínculo" style={{ ...btnStyle(C.brand, true), padding: "10px 12px" }}><Pencil size={15} /></button>
                <button onClick={() => excluir(v)} aria-label="Apagar vínculo" style={{ ...btnStyle(C.danger, true), padding: "10px 12px" }}><Trash2 size={15} /></button>
              </div>
            )}
          </div>
        ))}
      </div>
      {editando && (
        <ModalVinculo C={C} vinculo={editando} produtos={produtos.filter((p) => p.ativo)} unidades={unidades.filter((u) => u.ativo)}
          onClose={() => setEditando(null)}
          onSalvo={(novo) => { setVinculos((l) => l.map((x) => (x.id === novo.id ? novo : x))); setEditando(null); }} />
      )}
    </>
  );
}
